import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  BoxProps,
  Button,
  HStack,
  IconButton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import SlotComponent from './Slot';
import { DragBox } from '../dnd/DragBox';
import { DragLayer } from '../dnd/DragLayer';
import { useDrop } from 'react-dnd';
import { ItemTypes } from '../dnd/types';
import { shuffleArray } from '../../../../../../helpers/shuffle';
import { dirFromDndPos, flexDirFromDndPos } from '../shared/utils';
import { RefreshIcon } from '../../../../../../components/icons';

export type Slot = {
  key: string;
  index: number;
  answer: string;
  caseSensitive?: boolean;
};

interface FillMissingTextProps extends BoxProps {
  text: string;
  slots: Slot[];
  dragNDrop?: boolean;
  debug?: boolean;
  showAnswer?: boolean;
  showCorrect?: boolean;
  dndPosition?: string;
  isViewOnly?: boolean;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: State;
}

type State = {
  answers: Record<string, { value: string; correct: boolean }>;
  slot: Record<string, string>;
};

const FillMissingText: React.FC<FillMissingTextProps> = ({
  text,
  slots,
  dragNDrop,
  debug,
  showAnswer,
  showCorrect,
  dndPosition = 'bottom',
  isViewOnly,
  submit,
  reset,
  initialState,
  ...props
}) => {
  const { t } = useTranslation();
  const textParts = text.split('xy_____yx');
  const [state, setState] = useState<State>({ answers: {}, slot: {} });
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<string>('');
  const indexMap = useMemo(() => {
    const map: Map<number, Slot> = new Map();

    slots.forEach((s) => {
      map.set(s.index, s);
    });

    return map;
  }, [slots]);

  const shuffedSlots = useMemo(() => {
    const shuffedSlots = [...slots];

    shuffleArray(shuffedSlots);

    return shuffedSlots;
  }, [slots]);

  const [{ hovering }, drop] = useDrop({
    accept: ItemTypes.DragBox,
    drop: (item: { id: string }, monitor) => {
      if (showResults || isViewOnly) return;
      if (monitor.isOver({ shallow: true })) {
        let newState = { ...state };
        const answerKey = newState.slot[item.id]!;
        delete newState.answers[answerKey];
        delete newState.slot[item.id];
        setState(newState);
      }
    },
    collect: (monitor) => ({
      hovering: monitor.isOver(),
    }),
  });

  const updateValueFn = (toKey: string, newValue: string, fromKey: string) => {
    let newState = { ...state };
    let oldAnswerKey = newState.slot[fromKey];
    let oldSlot = Object.keys(newState.slot).find(
      (s) => newState.slot[s] === toKey,
    );
    if (oldAnswerKey) {
      if (newState.answers[toKey] != null) {
        newState.answers[oldAnswerKey] = { ...newState.answers[toKey]! };
      } else {
        delete newState.answers[oldAnswerKey];
      }
    }
    if (oldSlot) {
      if (newState.slot[oldSlot] != null) {
        newState.slot[oldSlot] = oldAnswerKey!;
      }
    }
    newState.slot[fromKey] = toKey;
    newState.answers[toKey]! = {
      value: newValue,
      correct: false,
    };
    Object.keys(newState.answers).forEach((key) => {
      const slot = slots.find((s) => s.key === key);
      if (slot) {
        newState.answers[key]!.correct = slot.caseSensitive
          ? newState.answers[key]!.value === slot.answer
          : newState.answers[key]!.value.toLocaleLowerCase() ===
            slot.answer.toLocaleLowerCase();
      }
    });
    setState(newState);
  };

  const announce = (msg: string) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(msg));
  };

  const handlePickItem = (id: string) => {
    if (showResults || isViewOnly) return;
    if (selectedItemId === id) {
      setSelectedItemId(null);
      announce('Selection cancelled.');
    } else {
      setSelectedItemId(id);
      const item = shuffedSlots.find(s => s.key === id);
      announce(`${item?.answer ?? 'Item'} selected. Tab to a blank and press Space or Enter to place it.`);
    }
  };

  const handleDropOnSlot = (slotKey: string) => {
    if (!selectedItemId || showResults || isViewOnly) return;
    const item = shuffedSlots.find(s => s.key === selectedItemId);
    if (!item) return;
    updateValueFn(slotKey, item.answer, item.key);
    announce(`${item.answer} placed.`);
    setSelectedItemId(null);
  };

  const removeFromSlot = (slotKey: string) => {
    const itemId = Object.keys(state.slot).find(k => state.slot[k] === slotKey);
    if (!itemId) return;
    const newState = {
      answers: { ...state.answers },
      slot: { ...state.slot },
    };
    delete newState.answers[slotKey];
    delete newState.slot[itemId];
    setState(newState);
    setSelectedItemId(null);
  };

  const setInitialState = () => {
    setState({ answers: {}, slot: {} });
    setSelectedItemId(null);
  };

  useEffect(() => {
    if (initialState) {
      setState(initialState);
      setShowResults(true);
      let correct = 0;

      Object.keys(initialState.answers).forEach((key) => {
        if (initialState.answers[key]!.correct) {
          correct += 1;
        }
      });

      setShownCorrect(correct);
    } else setInitialState();
  }, [slots, initialState]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    Object.keys(state.answers).forEach((key) => {
      if (state.answers[key]!.correct) {
        correct += 1;
      }
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, slots.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    setSelectedItemId(null);
    if (reset != null) reset();
  };

  const ret = (
    <VStack
      w="full"
      spacing={0}
      gap={4}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && selectedItemId) {
          setSelectedItemId(null);
          announce('Selection cancelled.');
        }
      }}
    >
      <Box
        aria-live="polite"
        aria-atomic="true"
        position="absolute"
        w="1px"
        h="1px"
        overflow="hidden"
        style={{ clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
      >
        {announcement}
      </Box>
      <Stack
        {...props}
        ref={drop}
        alignItems="center"
        flexDir={flexDirFromDndPos(dndPosition)}
        gap={4}
        display="flex"
      >
        {/* @ts-ignore */}
        <pre style={{ textWrap: 'wrap' }}>
          {textParts.map((part, index) => {
            if (indexMap.has(index)) {
              const slot = indexMap.get(index)!;
              const s2 = Object.keys(state.slot).find(
                (s) => state.slot[s] === slot.key,
              );
              return (
                <>
                  <span>{part}</span>
                  <SlotComponent
                    id={slot.key}
                    key={slot.key}
                    slot={s2}
                    value={state.answers[slot.key]?.value}
                    answer={slot.answer}
                    showCorrect={showCorrect}
                    isViewOnly={isViewOnly}
                    showResults={showResults}
                    updateValue={showResults || isViewOnly ? () => {} : updateValueFn}
                    onKeyboardDrop={() => handleDropOnSlot(slot.key)}
                    hasKeyboardSelection={selectedItemId !== null}
                    onRemove={() => removeFromSlot(slot.key)}
                  />
                </>
              );
            }
            return <span>{part}</span>;
          })}
        </pre>
        <Stack
          spacing={0}
          gap={2}
          flexGrow={1}
          background={hovering ? 'rgba(208,208,208,0.45)' : undefined}
          borderRadius="8px"
          direction={dirFromDndPos(dndPosition)}
          w={dirFromDndPos(dndPosition) === 'row' ? 'full' : undefined}
          minH={dirFromDndPos(dndPosition) === 'row' ? '40px' : undefined}
          minW={dirFromDndPos(dndPosition) === 'column' ? '120px' : undefined}
          align="center"
          alignSelf={
            dirFromDndPos(dndPosition) === 'column' ? 'stretch' : undefined
          }
        >
          {shuffedSlots
            .filter((s) => state.slot[s.key] === undefined)
            .map((s) => (
              <DragBox
                id={s.key}
                key={s.key}
                text={s.answer}
                boxShadow="sm"
                noDrag={showResults || isViewOnly}
                onKeyboardSelect={() => handlePickItem(s.key)}
                isKeyboardSelected={selectedItemId === s.key}
              />
            ))}
          <div />
        </Stack>
      </Stack>
      <HStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <>
            {!isViewOnly && (
              <IconButton
                size="sm"
                colorScheme="stemiBlue"
                variant="ghost"
                aria-label="reset"
                icon={<RefreshIcon boxSize="24px" />}
                onClick={_reset}
              >
                {t('fill.reset')}
              </IconButton>
            )}
            <HStack>
              <span>
                {shownCorrect} / {slots.length}
              </span>
            </HStack>
          </>
        )}
        <HStack>
          {!isViewOnly && (
            <Button colorScheme="blue" onClick={_submit}>
              {t('fill.submit')}
            </Button>
          )}
        </HStack>
      </HStack>
    </VStack>
  );

  if (debug) {
    return (
      <VStack w="full">
        {ret}
        <DragLayer />
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return (
    <>
      <DragLayer />
      {ret}
    </>
  );
};

export default FillMissingText;
